// SVG renderer for a packed scene. Layers: paper → anchor wash → region washes
// → accent symbols → hand-drawn text boxes → text → connectors → (debug regions).
// Text boxes are dressed to the terrain they landed in (board/terrain.js), or
// take a motif that says what their text is (board/motifs.js, TT-1269).

import { terrainAt, terrainDressing, terrainFill, terrainOutline, terrainTrim, walk } from './terrain.js';
import { MOTIF_TINT, isMotif, motifOutline, motifStyle, motifTrim } from './motifs.js';
import { mountArt } from './motifArt.js';
import { pickArtPiece } from '../lib/artPick.js';

const NS = 'http://www.w3.org/2000/svg';
export const FONTS = {
  title: "'Permanent Marker', 'Patrick Hand', cursive",
  body:  "'Patrick Hand', 'Kalam', cursive",
  quote: "'Caveat', 'Patrick Hand', cursive",
};

export function makeMeasure() {
  const c = document.createElement('canvas').getContext('2d');
  return (text, px, fontKey) => { c.font = `${px}px ${FONTS[fontKey] || FONTS.body}`; return c.measureText(text).width; };
}

// Small seeded PRNG so jitter is reproducible per box.
export function rng(seed) { let s = seed >>> 0 || 1; return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296; }
export function hash(str) { let h = 2166136261; for (const ch of str) h = Math.imul(h ^ ch.charCodeAt(0), 16777619); return h >>> 0; }

export function setAttrs(node, attrs) {
  if (node) for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}
export function el(tag, attrs = {}, parent) {
  const e = setAttrs(document.createElementNS(NS, tag), attrs);
  if (parent) parent.appendChild(e);
  return e;
}

// Hand-drawn rounded rect: walk the perimeter with small perpendicular wobble.
export function sketchRect(r, seed, wobble = 2.2, inset = 0) {
  const x = r.x + inset, y = r.y + inset, w = r.w - inset * 2, h = r.h - inset * 2, rad = Math.min(18, w / 4, h / 4);
  return walk([[x + rad, y, x + w - rad, y, 12], [x + w, y + rad, x + w, y + h - rad, 6], [x + w - rad, y + h, x + rad, y + h, 12], [x, y + h - rad, x, y + rad, 6]], rng(seed), wobble) + 'Z';
}

// A text box's `dress` is a motif name, a terrain name, or null for the plain box.
// Its outline: the motif, the terrain silhouette, else the sketch rect.
export function textBoxOutline(dress, r, seed, wobble) {
  return (dress && (motifOutline(dress, r, rng(seed), wobble) || terrainOutline(dress, r, rng(seed), wobble))) || sketchRect(r, seed, wobble);
}
export function textBoxFill(dress, fallback) {
  return MOTIF_TINT[dress]?.fill ?? terrainFill(dress, fallback);
}
// A box's dress: the motif the plan names (TT-1269) wins over the board's
// terrain coin, rolled once per landing from the box's seed.
export function textBoxDress(box, regions, seed) {
  return box.motif || terrainDressing(terrainAt(box, regions), rng(seed + 11));
}
/**
 * What a dressed text box draws, as attributes per layer — the static
 * renderer makes elements of them, the live one updates its nodes — so the
 * two boards ink the same way. A painted piece (`art`, the text-box-art
 * manifest) replaces the drawn outline: the paths are empty and `trim` is
 * off. Seeds: shade +7, pencil +3, ink 0, trim +13.
 */
export function textBoxPaint(dress, rect, seed, pal, art) {
  const piece = art?.[dress] || null;
  const style = motifStyle(dress);
  const outline = (sd, wobble) => (piece ? '' : textBoxOutline(dress, rect, sd, wobble));
  return {
    piece,
    shade: { d: style.shade ? outline(seed + 7, 2.5) : '' },
    pencil: { d: outline(seed + 3, 3.5), 'stroke-width': style.strokeWidth ? 1.5 : 0 },
    ink: { d: outline(seed, 2.2), fill: textBoxFill(dress, '#ffffff'), stroke: style.ink || pal.ink, 'stroke-width': style.strokeWidth },
    trim: Boolean(dress && !piece),
  };
}

// The board's art, anchors and stickers alike: symbol_id → { url, stages:
// [urls], intro: url | null }, from the catalog's board manifest the host
// hands over (BoardCanvas useBoardArt). A symbol with no art draws none. A catalog entry also
// lists the symbol's active pieces by style: `styles` (a host's
// artStyles) picks among them (lib/artPick.js); none draws the default.
export const artPiece = (boardArt, symbolId, styles) => pickArtPiece(boardArt?.[symbolId], styles);
export const artHref = (boardArt, symbolId, styles) => artPiece(boardArt, symbolId, styles)?.url || null;
// Fallback only — a plan always carries `palette`, authored by the planner
// (platform domain/scenegen/board.py PALETTE). Keep these identical to it.
export const PALETTE = { ink: '#1f1d1a', accent: '#b8641f', accent2: '#3b6c9c', shade: '#b8b2a7' };

// Shared SVG scaffolding: viewBox, paper (white with a faint grain so the
// washes carry the colour), wash blur, arrow markers. Returns <defs>.
export function mountPaper(svg, panel, pal) {
  svg.innerHTML = '';
  svg.setAttribute('viewBox', `0 0 ${panel.w} ${panel.h}`);
  const defs = el('defs', {}, svg);
  const paperF = el('filter', { id: 'paper' }, defs);
  el('feTurbulence', { type: 'fractalNoise', baseFrequency: '0.9', numOctaves: '2', result: 'n' }, paperF);
  el('feColorMatrix', { in: 'n', type: 'saturate', values: '0' }, paperF);
  el('feBlend', { in: 'SourceGraphic', in2: 'n', mode: 'multiply' }, paperF);
  const washF = el('filter', { id: 'wash', x: '-20%', y: '-20%', width: '140%', height: '140%' }, defs);
  el('feGaussianBlur', { stdDeviation: '28' }, washF);
  const washSm = el('filter', { id: 'wash-sm', x: '-30%', y: '-50%', width: '160%', height: '200%' }, defs);
  el('feGaussianBlur', { stdDeviation: '4' }, washSm);
  for (const [id, fill, size] of [['arrow', pal.ink, 7], ['arrow-spine', pal.accent, 5]]) {
    const marker = el('marker', { id, viewBox: '0 0 10 10', refX: '8', refY: '5', markerWidth: size, markerHeight: size, orient: 'auto-start-reverse' }, defs);
    el('path', { d: 'M0,0 L10,5 L0,10 L3,5 Z', fill }, marker);
  }
  el('rect', { width: panel.w, height: panel.h, fill: '#ffffff' }, svg);
  el('rect', { width: panel.w, height: panel.h, fill: '#f7f5f0', opacity: 0.18, filter: 'url(#paper)' }, svg);
  return defs;
}

// point on a box's edge, 10px out, along the ray from its centre to (tx, ty)
export function edge(box, tx, ty) {
  const cx = box.x + box.w / 2, cy = box.y + box.h / 2, dx = tx - cx, dy = ty - cy;
  const sx = Math.abs(dx) > 1e-6 ? (box.w / 2 + 10) / Math.abs(dx) : Infinity, sy = Math.abs(dy) > 1e-6 ? (box.h / 2 + 10) / Math.abs(dy) : Infinity;
  const s = Math.min(sx, sy); return [cx + dx * s, cy + dy * s];
}

// The dress's trim (grass, ripples, roots, leaves; a motif's rays, roll or
// gutter) as paths under `g`. The washes share one blurred group: a filter
// region per blob would re-rasterise three times per box on every frame of a drift.
export function mountTrim(g, dress, rect, rand) {
  let washes = null;
  for (const t of isMotif(dress) ? motifTrim(dress, rect, rand) : terrainTrim(dress, rect, rand)) {
    if (t.kind === 'wash' && !washes) washes = el('g', { filter: 'url(#wash-sm)' }, g);
    el('path', { d: t.d, stroke: t.stroke, fill: t.fill, 'stroke-width': t.strokeWidth, opacity: t.opacity, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, t.kind === 'wash' ? washes : g);
  }
}

export function renderScene(svg, scene, plan, opts = {}) {
  const { panel } = scene;
  const boardArt = opts.boardArt || {}; // symbol_id → art (the board art manifest)
  const styles = opts.artStyles; // the styles to draw that art in, if any
  const art = opts.textBoxArt || {}; // mood → painted piece (text-box-art manifest)
  const pal = { ...PALETTE, ...(plan.palette || {}) };
  const seedBase = hash(plan.title?.text || 'scene');
  mountPaper(svg, panel, pal);

  // anchor wash (background role)
  const anchorHref = scene.anchor && artHref(boardArt, scene.anchor.symbolId, styles);
  if (anchorHref) {
    const a = scene.anchor;
    el('image', { href: anchorHref, x: a.x - a.h / 2, y: a.y - a.h / 2, width: a.h, height: a.h, opacity: a.opacity, preserveAspectRatio: 'xMidYMid meet' }, svg);
  }

  // region washes: a soft blob of the chunk hue behind each chunk (wash-behind-ink)
  for (const b of scene.boxes.filter(b => b.kind === 'chunk')) {
    const c = pal[b.hue] || pal.accent;
    el('ellipse', { cx: b.x + b.w / 2, cy: b.y + b.h / 2, rx: b.w * 0.6, ry: b.h * 0.7, fill: c, opacity: 0.10, filter: 'url(#wash)' }, svg);
  }

  // connectors
  for (const c of scene.connectors) {
    const ax = c.a.x + c.a.w / 2, ay = c.a.y + c.a.h / 2, bx = c.b.x + c.b.w / 2, by = c.b.y + c.b.h / 2;
    const [x0, y0] = edge(c.a, bx, by), [x1, y1] = edge(c.b, ax, ay);
    const mx = (x0 + x1) / 2 + (y1 - y0) * 0.15, my = (y0 + y1) / 2 - (x1 - x0) * 0.15;
    el('path', {
      d: `M${x0},${y0} Q${mx},${my} ${x1},${y1}`, fill: 'none', stroke: pal.ink, 'stroke-width': 3,
      'stroke-dasharray': c.style === 'dashed' ? '10 8' : 'none', 'stroke-linecap': 'round',
      'marker-end': c.kind === 'arrow' ? 'url(#arrow)' : 'none',
    }, svg);
  }

  // text boxes + text
  for (const b of scene.boxes.filter(b => b.kind === 'chunk' || b.kind === 'title')) {
    const seed = seedBase ^ hash(b.id || 'title');
    const rand = rng(seed);
    const g = el('g', { transform: `rotate(${(rand() - 0.5) * 2.4} ${b.x + b.w / 2} ${b.y + b.h / 2})` }, svg);
    if (b.kind === 'chunk' && !b.container && b.lines.length) {
      // loose text: a quick highlighter swipe under the first line instead of a frame
      const h0 = b.lines[0];
      const w = Math.min(b.w - h0.x * 2, h0.text.length * h0.px * 0.5);
      el('path', { d: `M${b.x + h0.x - 2},${b.y + h0.y + 2} q${w / 2},${5} ${w},0`, stroke: pal[b.hue] || pal.accent, 'stroke-width': h0.px * 0.45, fill: 'none', 'stroke-linecap': 'round', opacity: 0.28 }, g);
    }
    if (b.kind === 'chunk' && b.container) {
      // motif, terrain silhouette (cloud, boulder, float…) or the plain sketch rect;
      // grey shading offset bottom-right (second-pass look), then the ink outline, then trim —
      // or the painted piece (opts.textBoxArt, TT-1269) instead of all three
      const dress = textBoxDress(b, scene.regions, seed);
      const p = textBoxPaint(dress, b, seed, pal, art);
      if (p.piece) mountArt(el('g', { class: 'art' }, g), p.piece, b, el);
      if (p.shade.d) el('path', { ...p.shade, fill: pal.shade, opacity: 0.35, transform: 'translate(5,6)' }, g);
      if (p.ink.d) el('path', { ...p.ink, 'fill-opacity': 0.72, 'stroke-linejoin': 'round' }, g);
      if (p.trim) mountTrim(g, dress, b, rng(seed + 13));
    }
    for (const ln of b.lines) {
      el('text', {
        x: b.x + ln.x, y: b.y + ln.y - ln.px * 0.15, 'font-size': ln.px, 'font-family': FONTS[ln.font],
        fill: pal[ln.color] || pal.ink, 'letter-spacing': ln.cls === 'title' ? 1 : 0,
      }, g).textContent = ln.text;
    }
    if (b.kind === 'title') {
      // underline banner stroke
      const t = b.lines[0];
      const w = t ? Math.min(b.w, t.text.length * t.px * 0.55) : b.w;
      el('path', { d: `M${b.x + 4},${b.y + b.h - 6} q${w / 2},${8} ${w},0`, stroke: pal.accent, 'stroke-width': 5, fill: 'none', 'stroke-linecap': 'round', opacity: 0.8 }, g);
    }
  }

  // accent symbols — on top, since stickers overlap their chunk's frame
  for (const b of scene.boxes.filter(b => b.kind === 'symbol' && artHref(boardArt, b.symbolId, styles))) {
    el('image', { href: artHref(boardArt, b.symbolId, styles), x: b.x, y: b.y, width: b.w, height: b.h, preserveAspectRatio: 'xMidYMid meet' }, svg);
  }

  if (opts.debugRegions) {
    for (const r of scene.regions) {
      el('polygon', { points: r.poly.map(p => p.join(',')).join(' '), fill: 'none', stroke: '#d33', 'stroke-width': 1.5, 'stroke-dasharray': '6 5', opacity: 0.7 }, svg);
      const [x, y] = r.poly[0];
      el('text', { x: x + 6, y: y + 16, 'font-size': 14, fill: '#d33', 'font-family': 'monospace' }, svg).textContent = `${r.name} · ${r.role}`;
    }
  }
}
